import { FormHelperText } from '@mui/material';
import { memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useMedia } from 'react-media';
import styled, { css } from 'styled-components';

import DateRangePicker from '@/components/common/Inputs/DateRangePicker/DateRangePicker';
import ComponentDisabler from '@/components/common/Loaders/ComponentDisabler';
import { DatePickerRange } from '@/domain/requestsPage';
import { COLOR, COLOR_FUNCTION, GLOBAL_MEDIA_QUERIES } from '@/globalStyles';
import { MIXPANEL_ACTIONS, mixpanelTracking } from '@/mixPanel/mixPanelActions';
import useCalendarLogic from '@/pages/NewRequest/Calendar/useCalendarLogic';
import { NEW_REQUEST_FIELD } from '@/pages/NewRequest/useNewRequestLogic';

interface ICalendarProps {
    className?: string;
    disabled?: boolean;
    isLimitExceeded?: boolean;
    allowOverbooking?: boolean;
    exceededNumberOfDays?: number;
    dateRange: DatePickerRange;
    onChange: (name, dates: DatePickerRange) => void;
    error?: string;
    reloadDataOnReset?: unknown[];
    touched: boolean;
    userId?: string;
    hideOverlappingError?: boolean;
}

const Calendar = ({
    isLimitExceeded,
    allowOverbooking,
    exceededNumberOfDays,
    disabled,
    dateRange,
    onChange,
    error,
    reloadDataOnReset,
    className,
    touched,
    userId,
    hideOverlappingError,
}: ICalendarProps) => {
    const { t } = useTranslation();
    const matches = useMedia({ queries: GLOBAL_MEDIA_QUERIES });
    const showDesktopVersion = matches?.tablet;
    const { calendarConfig, allRequestsWithColors, isRangesOverlapping } = useCalendarLogic({
        dateRange,
        reloadDataOnReset,
        userId,
        showDesktopVersion,
    });

    const onRangeChange = useCallback(
        newRange => {
            mixpanelTracking(MIXPANEL_ACTIONS.DATE_RANGE_NEW_REQUEST);
            onChange(NEW_REQUEST_FIELD.dateRange, newRange);
        },
        [onChange]
    );

    return (
        <ComponentDisabler isDisabled={disabled}>
            <div className={className} data-testid="calendar">
                <CalendarWrapper isError={isLimitExceeded}>
                    <DateRangePicker
                        range={dateRange}
                        onChange={onRangeChange}
                        customConfig={calendarConfig}
                        showHolidays
                        existedRequests={allRequestsWithColors}
                    />
                </CalendarWrapper>

                {!hideOverlappingError && isRangesOverlapping && (
                    <FormHelperText error>{t('overlapsWithOtherAbsence_message')}</FormHelperText>
                )}
                {isLimitExceeded && allowOverbooking && (
                    <FormHelperText error>
                        {t('error_absenceLimitExceeded_message_days', { count: exceededNumberOfDays })}
                    </FormHelperText>
                )}
                {isLimitExceeded && !allowOverbooking && (
                    <FormHelperText error>{t('error_absenceLimitExceeded_message')}</FormHelperText>
                )}
                {touched && error && <FormHelperText error>{error}</FormHelperText>}
            </div>
        </ComponentDisabler>
    );
};

const CalendarWrapper = styled.div<{ isError?: boolean }>`
    ${({ isError }) =>
        isError &&
        css`
            .rdrDateDisplayWrapper .rdrDateInput.rdrDateDisplayItem {
                border-color: ${COLOR.redWildWatermelon};
                &.rdrDateDisplayItemActive {
                    border-color: ${COLOR.redWildWatermelon};
                }
            }
        `}
`;

const Memoized = memo(Calendar);
const Styled = styled(Memoized)`
    .rdrCalendarWrapper {
        width: 100%;
        align-items: center;
        border: 1px solid ${COLOR_FUNCTION.border};
    }
`;

export default Styled;
